/**
 * BudgetPill — Compact sticky bar showing budget status.
 *
 * Displays: $87.3M / $100.0M + thin progress bar + chip badge
 * Color shifts (green -> amber -> red) as budget approaches/exceeds cap.
 */

import { Infinity as InfinityIcon, Zap } from "lucide-react";
import { m } from "@/paraglide/messages";
import type { ChipType } from "../../lib/algorithms";

interface BudgetPillProps {
  currentSpending: number;
  budgetCap: number;
  activeChip: ChipType;
  className?: string;
}

export function BudgetPill({
  currentSpending,
  budgetCap,
  activeChip,
  className = "",
}: BudgetPillProps) {
  const isLimitless = activeChip === "limitless";
  const ratio = isLimitless ? 0 : currentSpending / budgetCap;
  const remaining = budgetCap - currentSpending;
  const chipLabels: Record<string, string> = {
    x3: "x3",
    limitless: m.f1_chip_limitless(),
    "no-negative": m.f1_chip_no_neg(),
  };

  // Color based on budget usage
  const getColor = () => {
    if (isLimitless) return "text-accent";
    if (ratio > 1) return "text-destructive";
    if (ratio > 0.9) return "text-amber-500";
    return "text-success";
  };

  const getBarColor = () => {
    if (isLimitless) return "bg-accent";
    if (ratio > 1) return "bg-destructive";
    if (ratio > 0.9) return "bg-amber-500";
    return "bg-success";
  };

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2 rounded-xl border border-border bg-card/80 backdrop-blur-sm ${className}`}
    >
      {/* Budget text */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        {isLimitless ? (
          <div className="flex items-center gap-1.5">
            <InfinityIcon className="h-4 w-4 text-accent" />
            <span className="font-mono text-sm font-bold text-accent">
              {m.f1_chip_limitless()}
            </span>
          </div>
        ) : (
          <>
            <span className={`font-mono text-sm font-bold ${getColor()}`}>
              ${currentSpending.toFixed(1)}M
            </span>
            <span className="text-muted-foreground text-xs">/</span>
            <span className="font-mono text-xs text-muted-foreground">
              ${budgetCap.toFixed(1)}M
            </span>
          </>
        )}
      </div>

      {/* Progress bar */}
      {!isLimitless && (
        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${getBarColor()}`}
            style={{ width: `${Math.min(ratio * 100, 100)}%` }}
          />
        </div>
      )}

      {/* Chip badge */}
      {activeChip && (
        <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-mono font-medium bg-accent/20 text-accent">
          <Zap className="h-3 w-3" />
          {chipLabels[activeChip] ?? activeChip}
        </span>
      )}

      {/* Remaining */}
      {!isLimitless && currentSpending > 0 && (
        <span
          className={`font-mono text-xs ${remaining >= 0 ? "text-muted-foreground" : "text-destructive font-bold"}`}
        >
          {remaining >= 0
            ? `+$${remaining.toFixed(1)}M`
            : `-$${Math.abs(remaining).toFixed(1)}M`}
        </span>
      )}
    </div>
  );
}
